import type React from 'react';

interface SvgMaskIconProps {
    /** SVG `viewBox` for the icon geometry, e.g. "0 0 24 24". */
    viewBox: string;
    /** Opaque inner SVG markup (shapes must use `fill="#000"` / `stroke="#000"`). The
     *  visible color comes from `color` via the CSS mask, not from this markup. */
    inner: string;
    /** Omit either dimension to size via `className` / `aspectRatio` instead. */
    width?: number;
    height?: number;
    /** CSS `aspect-ratio` (e.g. "2355 / 700") so a single fixed dimension derives the other. */
    aspectRatio?: string;
    /** Any CSS color — supports `currentColor` and `var(--…)`, which a data-URI `<img>`
     *  cannot resolve inside its isolated SVG document. */
    color: string;
    className?: string;
    style?: React.CSSProperties;
}

// Renders a monochrome icon as a masked <span> instead of inline React SVG. The browser
// parses the whole graphic as a CSS mask data-URI in one pass, so React never runs
// appendChild on individual SVG-namespace nodes — which some host pages monkeypatch to
// JSON.stringify DOM nodes, crashing our commit phase with "Converting circular structure
// to JSON" (IX-4048 / IX-4076). The mask keeps `color` a live CSS value (currentColor,
// CSS variables) — a data-URI <img> could not.
const SvgMaskIcon: React.FC<SvgMaskIconProps> = ({
    viewBox,
    inner,
    width,
    height,
    aspectRatio,
    color,
    className,
    style,
}) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}">${inner}</svg>`;
    const maskUrl = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
    return (
        <span
            aria-hidden="true"
            className={className}
            style={{
                display: 'inline-block',
                width,
                height,
                aspectRatio,
                backgroundColor: color,
                maskImage: maskUrl,
                WebkitMaskImage: maskUrl,
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskPosition: 'center',
                maskSize: 'contain',
                WebkitMaskSize: 'contain',
                ...style,
            }}
        />
    );
};

export default SvgMaskIcon;
